import React from 'react';
import { Map } from 'immutable';
import { useRouter } from 'next/router';
import {
  Icon,
  ListItem,
  ListItemProps,
  List,
  ListProps,
  Text,
  Button,
  Box,
} from '@opendoor/bricks/core';
import { useObservability } from '../../../helpers/observability';
import { AccountProps } from '../../shared/AccountConnector';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import Close16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/Close16';
import FavoriteIcon from '@opendoor/bricks/core/Icon/SpritesheetIcons/Favorite';
import { ExclusivesNavLink } from './Header';
import { getNavLinkStyles, NavLink } from './NavLink';
import { useOutsideClickAlerter } from '@opendoor/bricks/hooks/useOutsideClickAlerter';
import { useExclusivesTypeContext } from '../../../helpers/exclusives/ExclusivesTypeContext';

const PrimaryNavSubmenu: React.FC<Omit<ListProps, 'variant'> & { isExpanded: boolean }> = ({
  isExpanded,
  ...props
}) => (
  <List
    variant="text"
    top={[0, null, null, -6]}
    right={[0, null, null, -5]}
    zIndex={1}
    px={7}
    py={6}
    position="absolute"
    textAlign="right"
    whiteSpace="nowrap"
    // @ts-expect-error - this is a known deviation from the design system
    borderRadius={['10px 0 0 10px', null, null, '0 0 10px 10px']}
    transition="opacity ease 0.3s"
    fontWeight="semibold"
    bgColor="neutrals0"
    boxShadow="z1"
    visibility={isExpanded ? 'visible' : 'hidden'}
    opacity={isExpanded ? 1 : 0}
    {...props}
  />
);

const PrimaryNavItem: React.FC<Omit<ListItemProps, 'variant'>> = (props) => (
  <ListItem
    variant="text"
    fontSize="s0"
    position="relative"
    px="0.75em"
    alignItems="center"
    sx={{ '&:last-child': { pb: 0 } }}
    {...props}
  />
);

interface Props {
  isExpanded: boolean;
  onClose: () => void;
  loggedIn: boolean;
  human: AccountProps['human'];
  leads: AccountProps['leads'];
  maybeLogOut: AccountProps['maybeLogOut'];
  onFAQsClick?: () => void;
}

const HeaderMenu: React.FC<Props> = ({
  human,
  leads,
  maybeLogOut,
  isExpanded,
  onClose,
  loggedIn,
  onFAQsClick,
}) => {
  const { type } = useExclusivesTypeContext();
  const router = useRouter();
  const { trackEvent } = useObservability();
  const analyticsPrefix = `cosmos-${type}-header-menu`;
  const ref = React.useRef<HTMLDivElement>(null);

  const handleSignOutClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    // NOTE: this is also tracked in Journey as `cosmos-{exclusives|exclusive-offers}-header-menu-logout`
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    maybeLogOut();
  };

  // Only want to show offered leads
  const leadsToShow = leads.filter((l) => l.get('state') == 'offered');
  useOutsideClickAlerter(ref, () => onClose());

  return (
    <Box ref={ref}>
      <Box display="none">
        <SelectiveSpritesheet icons={[Close16, FavoriteIcon]} />
      </Box>
      <PrimaryNavSubmenu isExpanded={isExpanded}>
        <Button
          aria-label="Close header menu"
          analyticsName={`${analyticsPrefix}-close`}
          variant="icon"
          onClick={onClose}
          display="inline-flex"
          boxShadow="none"
          border="none"
        >
          <Icon name="close" size={16} />
        </Button>
        {loggedIn ? (
          <>
            {leadsToShow &&
              leadsToShow.slice(0, 3).map((lead: Map<string, any>) => (
                <PrimaryNavItem key={lead.get('id')} className="primary-nav-submenu-item">
                  <NavLink
                    analyticsName={`${analyticsPrefix}-lead-${lead.get('id')}`}
                    aria-label={`Dashboard for ${lead.getIn(['address', 'street'])}`}
                    href={lead.get('url')}
                  >
                    {lead.getIn(['address', 'street'])}
                    {lead.get('readable_state') && ` (${lead.get('readable_state')})`}
                  </NavLink>
                </PrimaryNavItem>
              ))}
            {leadsToShow && leadsToShow.size > 3 && (
              <PrimaryNavItem className="primary-nav-submenu-item">
                <NavLink
                  analyticsName={`${analyticsPrefix}-more-properties`}
                  aria-label="Click to view more properties"
                  href="/properties"
                >
                  More
                </NavLink>
              </PrimaryNavItem>
            )}
            {leadsToShow && leadsToShow.size > 0 && <hr />}
            {human?.get('has_resale_closing_dashboard') && (
              <PrimaryNavItem className="primary-nav-submenu-item">
                <NavLink
                  analyticsName={`${analyticsPrefix}-resale-closing-dashboard`}
                  aria-label={null}
                  href="/closing-dashboard"
                >
                  Closing dash
                </NavLink>
              </PrimaryNavItem>
            )}
            {human?.get('has_resale_closing_dashboard') && <hr />}
          </>
        ) : (
          <PrimaryNavItem>
            <NavLink
              analyticsName={`${analyticsPrefix}-login`}
              aria-label={null}
              href={`/exclusives/login?redirectTo=${encodeURIComponent(router.asPath)}`}
            >
              Sign in
            </NavLink>
          </PrimaryNavItem>
        )}
        {loggedIn && (
          <PrimaryNavItem display={['block', null, null, 'none']}>
            <NavLink
              borderRadius="roundedSquare"
              display="inline"
              href="/exclusives/saved-homes"
              aria-label="Go to Saved homes"
              analyticsName={`${analyticsPrefix}-saved-homes`}
              gridGap="0.25rem"
              alignItems="center"
            >
              <Icon name="favorite" size={16} verticalAlign="text-bottom" /> Your homes
            </NavLink>
          </PrimaryNavItem>
        )}
        <PrimaryNavItem display={['block', null, null, 'none']}>
          {type === 'exclusives' ? (
            <NavLink
              analyticsName={`${analyticsPrefix}-faqs`}
              aria-label={null}
              href="/exclusives/faq"
            >
              FAQ
            </NavLink>
          ) : (
            <Button
              variant="inline-secondary-link"
              sx={getNavLinkStyles()}
              analyticsName={`${analyticsPrefix}-faqs`}
              aria-label={null}
              onClick={() => onFAQsClick && onFAQsClick()}
            >
              FAQ
            </Button>
          )}
        </PrimaryNavItem>
        <PrimaryNavItem display={['block', null, null, 'none']}>
          <ExclusivesNavLink />
        </PrimaryNavItem>
        <PrimaryNavItem display={['block', null, null, 'none']}>
          <NavLink
            analyticsName={`${analyticsPrefix}-contact`}
            aria-label={null}
            href="tel:(512) 706-9938"
          >
            <Text display="inline" fontWeight="inherit" color="inherit" mr="0.3rem">
              Call:
            </Text>
            512-706-9938
          </NavLink>
        </PrimaryNavItem>
        {loggedIn && (
          <>
            <PrimaryNavItem className="primary-nav-submenu-item">
              <NavLink
                analyticsName={`${analyticsPrefix}-account`}
                aria-label={null}
                // TODO: remove categoryOverride
                // observability is initialized with 'cosmos' as the category for all events
                // NOTE: this is also tracked in Journey as `cosmos-{exclusives|exclusive-offers}-header-menu-account`
                deprecatedTrackingEvent={[
                  'cta-click',
                  'account-link',
                  undefined,
                  {
                    categoryOverride: 'homepage',
                  },
                ]}
                href="/account"
              >
                Account
              </NavLink>
            </PrimaryNavItem>
            <PrimaryNavItem className="primary-nav-submenu-item">
              <NavLink
                analyticsName={`${analyticsPrefix}-preferences`}
                aria-label={null}
                href="/exclusives/preferences"
              >
                Preferences
              </NavLink>
            </PrimaryNavItem>
            <PrimaryNavItem className="primary-nav-submenu-item">
              <Button
                variant="inline-secondary-link"
                fontSize="s0"
                color="neutrals100"
                textDecoration="none"
                _hover={{ color: 'brand50', textDecoration: 'underline' }}
                _active={{ color: 'brand50', textDecoration: 'underline' }}
                whiteSpace="nowrap"
                analyticsName={`${analyticsPrefix}-logout`}
                aria-label={null}
                onClick={handleSignOutClick}
              >
                Sign out
              </Button>
            </PrimaryNavItem>
          </>
        )}
      </PrimaryNavSubmenu>
    </Box>
  );
};

export default HeaderMenu;
